import type { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import { getPensionTypesAbove } from '@/utils/estimates/hotel/computeHotelPensionPrice';
import {
  hasAnyPrice,
  parseBlockHeader,
  parsePersonPricesLine,
} from './blockHelpers';
import { getGridColumnCount, parsePastedGrid } from './parsePastedGrid';
import {
  findDuplicateLabels,
  splitSideBySideBlocks,
  tagColumn,
} from './multiTariffPaste';
import { findOverlapsWithOtherTariffs } from './parsePastedPeriods';
import type {
  ChildBandsCount,
  ComparableTariff,
  ExcelPasteIssue,
  ExcelPasteResult,
  PastedPeriod,
  PastedPersonPrices,
} from './types';

export const PENSION_BLOCK_ROWS = 12;
export const PENSION_BLOCK_COLUMNS = 3;

/** Lines 9 to 12 of a pension block, in grid order. */
export const PENSION_BLOCK_LINES: Array<{
  line: number;
  pensionType: Exclude<HotelPensionType, 'SingleRoom'>;
}> = [
  { line: 9, pensionType: 'Breakfast' },
  { line: 10, pensionType: 'SemiPension' },
  { line: 11, pensionType: 'FullPension' },
  { line: 12, pensionType: 'AllInclusive' },
];

export interface PastedPensionTariff {
  name: string;
  periods: PastedPeriod[];
  prices: Array<PastedPersonPrices & { pensionType: HotelPensionType }>;
}

export interface PensionPasteContext {
  childBandsCount: ChildBandsCount;
  /** `pricingPensionType.value`: the pension the room prices already include. */
  basePensionType?: string;
  otherTariffs: ComparableTariff[];
}

export function parsePensionTariffBlock(
  text: string,
  { otherTariffs, ...context }: PensionPasteContext,
): ExcelPasteResult<PastedPensionTariff> {
  const rows = parsePastedGrid(text);
  if (
    rows.length !== PENSION_BLOCK_ROWS ||
    getGridColumnCount(rows) > PENSION_BLOCK_COLUMNS
  ) {
    return {
      value: null,
      errors: [{ code: 'pensionShape', params: { received: rows.length } }],
      warnings: [],
    };
  }

  const result = parsePensionTariffRows(rows, context);
  if (!result.value) return result;
  return {
    ...result,
    warnings: findOverlapsWithOtherTariffs(result.value.periods, otherTariffs),
  };
}

/**
 * Every pension tariff at once: the 12×3 blocks side by side, 3 columns per
 * tariff, empty slots skipped. Overlaps are checked once the tariffs are
 * matched with the cards (findPastedOverlaps).
 */
export function parsePensionTariffsBlock(
  text: string,
  context: Omit<PensionPasteContext, 'otherTariffs'>,
): ExcelPasteResult<PastedPensionTariff[]> {
  const rows = parsePastedGrid(text);
  if (rows.length !== PENSION_BLOCK_ROWS) {
    return {
      value: null,
      errors: [{ code: 'pensionShape', params: { received: rows.length } }],
      warnings: [],
    };
  }
  const columnCount = getGridColumnCount(rows);
  if (columnCount % PENSION_BLOCK_COLUMNS !== 0) {
    return {
      value: null,
      errors: [
        {
          code: 'tariffColumns',
          params: { received: columnCount, width: PENSION_BLOCK_COLUMNS },
        },
      ],
      warnings: [],
    };
  }

  const errors: ExcelPasteIssue[] = [];
  const tariffs: PastedPensionTariff[] = [];
  splitSideBySideBlocks(rows, PENSION_BLOCK_COLUMNS).forEach(
    ({ inColumn, rows: tariffRows }) => {
      const parsed = parsePensionTariffRows(tariffRows, context);
      errors.push(...tagColumn(parsed.errors, inColumn));
      if (parsed.value) tariffs.push(parsed.value);
    },
  );
  if (errors.length === 0 && tariffs.length === 0) {
    errors.push({ code: 'noCategoryTariff' });
  }
  errors.push(...findDuplicateLabels(tariffs.map(({ name }) => name)));

  if (errors.length > 0) return { value: null, errors, warnings: [] };
  return { value: tariffs, errors: [], warnings: [] };
}

/** Label, periods and prices of one 12×3 block already split into cells. */
function parsePensionTariffRows(
  rows: string[][],
  {
    childBandsCount,
    basePensionType,
  }: Omit<PensionPasteContext, 'otherTariffs'>,
): ExcelPasteResult<PastedPensionTariff> {
  const { name, periods, errors } = parseBlockHeader(rows);
  const pricedPensionTypes = getPensionTypesAbove(basePensionType);

  const prices = PENSION_BLOCK_LINES.map(({ line, pensionType }) => {
    const lineErrors: ExcelPasteIssue[] = [];
    const linePrices = parsePersonPricesLine(
      rows,
      line,
      childBandsCount,
      lineErrors,
    );
    if (hasAnyPrice(linePrices) && !pricedPensionTypes.includes(pensionType)) {
      lineErrors.push({
        code: 'pensionNotAboveBase',
        params: { line, base: basePensionType ?? '', pensionType },
      });
    }
    errors.push(...lineErrors);
    return { pensionType, ...linePrices };
  });

  if (errors.length > 0) return { value: null, errors, warnings: [] };
  return { value: { name, periods, prices }, errors: [], warnings: [] };
}
